import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LOGO_PINK } from "../lib/data";

const inputCls = "w-full rounded-2xl border border-zinc-300 px-5 py-3.5 text-sm outline-none focus:ring-2 focus:ring-pink mb-4";
const labelCls = "block text-[11px] font-bold tracking-[0.25em] uppercase text-zinc-500 mb-2";

export const ResetPasswordPage = ({ portal }) => {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const loginPath = portal === "admin" ? "/admin/login" : "/retailer/login";
    const portalName = portal === "admin" ? "Backstage" : "Retailer Portal";

    const submit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Passwords don't match.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="min-h-[100svh] bg-ink tent-stripes flex items-center justify-center px-5">
            <div className="w-full max-w-sm bg-white rounded-3xl p-8" data-testid={`${portal}-reset-password-page`}>
                <img src={LOGO_PINK} alt="Showtime Carnival Co." className="h-16 w-auto mx-auto mb-6" />
                <h1 className="font-oswald uppercase text-2xl text-center mb-2">Set New Password</h1>
                <p className="text-xs text-zinc-500 text-center mb-6">{portalName}</p>
                {!token && !done && (
                    <p className="text-sm text-red-500 text-center" data-testid="reset-missing-token">This reset link is missing its token. Request a new one from the login page.</p>
                )}
                {done ? (
                    <div className="text-center" data-testid="reset-success">
                        <p className="text-sm text-zinc-600 mb-6">Your password is updated.</p>
                        <Link to={loginPath} data-testid="reset-goto-login" className="inline-block rounded-full bg-pink px-8 py-3.5 font-oswald uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors">
                            Log In
                        </Link>
                    </div>
                ) : token ? (
                    <form onSubmit={submit} data-testid={`${portal}-reset-form`}>
                        <label htmlFor={`${portal}-new-pw`} className={labelCls}>New password (min 8 characters)</label>
                        <input id={`${portal}-new-pw`} type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-new-password" />
                        <label htmlFor={`${portal}-confirm-pw`} className={labelCls}>Confirm new password</label>
                        <input id={`${portal}-confirm-pw`} type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="reset-confirm-password" />
                        <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full bg-pink py-4 font-oswald uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
                            {loading ? "Updating..." : "Update Password"}
                        </button>
                    </form>
                ) : null}
                {!done && (
                    <p className="text-center mt-5">
                        <Link to={loginPath} className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-pink-dark">Back to login</Link>
                    </p>
                )}
            </div>
        </section>
    );
};
